import { useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

/**
 * How the drawer's scrolling element is found from inside a tab panel.
 *
 * A panel's list does not own its scroll container — `SheetBody` does, several
 * levels up — so a virtualiser has to be pointed at that ancestor. The `data-slot`
 * attribute is the stable hook the sheet primitive already publishes for exactly
 * this kind of reach.
 */
export const SHEET_BODY_SCROLLER = '[data-slot="sheet-body"]';

export interface SheetBodyScroller {
  /** For `useVirtualizer({ getScrollElement })`: the drawer body, once found. */
  getScrollElement: () => HTMLElement | null;
  /**
   * Where the list starts inside the scrolling element. A virtualiser works in
   * that element's coordinates, and a list sits below a header and a tab strip
   * rather than at the body's top, so rows are placed at `start - scrollMargin`.
   */
  scrollMargin: number;
}

/**
 * The one way a drawer list drives the drawer's scroller (spec 096 FR-018).
 *
 * Both the executions rail and the comments timeline are virtualised lists that
 * must scroll *with* the drawer rather than inside a box of their own — a nested
 * scroller with no fixed height above it left the comments list a sliver the
 * moment the composer grew. The mechanism is the same for both: find `SheetBody`
 * from the list, measure the list's offset inside it, hand the virtualiser that
 * element and that margin. It lived twice, line for line; this is the once.
 *
 * `remeasureKey` re-runs the measurement when the list first appears (a panel
 * whose rows mount only after a fetch) — the layout effect's first pass may
 * find no list at all.
 */
export function useSheetBodyScroller(
  listRef: RefObject<HTMLElement | null>,
  remeasureKey: unknown = null,
): SheetBodyScroller {
  const scrollRef = useRef<HTMLElement | null>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const scroller = list.closest<HTMLElement>(SHEET_BODY_SCROLLER);
    scrollRef.current = scroller;
    if (!scroller) return;
    setScrollMargin(
      list.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top +
        scroller.scrollTop,
    );
    // `listRef` is a stable ref object; `remeasureKey` is the caller's signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remeasureKey]);

  return { getScrollElement: () => scrollRef.current, scrollMargin };
}
